// Generated by tutorials/record.cjs from tutorials/articles/h16-favorites-and-print-queue.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h16 --text-only` (wording) or
// `node tutorials/record.cjs h16` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'favorites-and-print-queue',
  catalogId: 'h16',
  title: 'Favorites and the print queue',
  summary: 'Star the reports you use every day so they are always close at hand, and use the Print Queue to see what is ready, open or print a report again, and make one again.',
  audience: 'Everyone who prints or saves reports',
  area: 'reports',
  learn: [
    'Star a report and find it under Favorites',
    'See the reports you run most',
    'Read the Print Queue and what each status means',
    'Open or print a report again',
    'See a report\'s details, and make it again with Reprocess',
  ],
  keywords: [
    'favorites',
    'favourites',
    'star',
    'starred reports',
    'recently run',
    'print queue',
    'reprint',
    'print again',
    'report status',
    'pending',
    'completed',
    'printed',
    'no data',
    'error',
    'reprocess',
    'details',
    'all users',
  ],
  steps: [
    {
      id: 'star',
      part: 'Star your favorite reports',
      heading: 'Star a report',
      text: 'On the Reports page, point to a report\'s card and select the star at its top right. You can also select the star at the top of a report\'s options.\n\nSelect the star again to take it off your favorites.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/star.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/favorites-and-print-queue/star.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'The Diet Order card with its star filled in, and a message: Added "Diet Order" to favorites.',
      },
    },
    {
      id: 'favorites-filter',
      part: 'Star your favorite reports',
      heading: 'See just your favorites',
      text: 'Select Favorites at the top of the Reports page to see only the reports you starred.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/favorites-filter.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/favorites-and-print-queue/favorites-filter.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.7,
        },
        alt: 'The Reports page filtered to Favorites: Diet Order, Food Allergies and Weight - Significant Change.',
      },
    },
    {
      id: 'favorites-menu',
      part: 'Star your favorite reports',
      heading: 'Favorites in the menu',
      text: 'Your favorites are also listed in the menu, under Reports, so you can open one from any page.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/favorites-menu.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Reports list in the menu, with a Favorites group holding Diet Order, Food Allergies and Weight - Significant Change.',
      },
    },
    {
      id: 'recently-run',
      part: 'Star your favorite reports',
      heading: 'The reports you run most',
      text: 'Select All to see every report again. Recently run, at the top, lists the reports you make most often, so the ones you use are first.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/recently-run.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/favorites-and-print-queue/recently-run.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.2,
        },
        alt: 'The Recently run section at the top of the Reports page.',
      },
    },
    {
      id: 'open-queue',
      part: 'Use the Print Queue',
      heading: 'Open the Print Queue',
      text: 'Select Print Queue under Reports. It lists the reports you made, newest first. The number beside Print Queue is how many are ready and not opened yet.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/open-queue.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/favorites-and-print-queue/open-queue.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.6,
        },
        alt: 'The Print Queue: each report with its name, date, when it was created and its status, and a 2 beside Print Queue in the menu.',
      },
    },
    {
      id: 'statuses',
      part: 'Use the Print Queue',
      heading: 'What the status means',
      text: 'Pending and Generating: Dietech is still making it. Completed: ready to open. Viewed: it has been opened on screen. Printed: it has been printed. No Data: nothing matched its options. Error: it could not be made.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/statuses.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Status column: Completed, Printed, No Data and Error.',
      },
    },
    {
      id: 'open-again',
      part: 'Use the Print Queue',
      heading: 'Open or print a report again',
      text: 'Select a report, then Print. It opens on screen, where you can print it or download it as PDF, Excel or Word. This works for any Completed, Viewed or Printed report, as often as you need.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/open-again.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/favorites-and-print-queue/open-again.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.3,
        },
        alt: 'The Food Allergies report open in the report viewer, over the Print Queue.',
      },
    },
    {
      id: 'last-printed',
      part: 'Use the Print Queue',
      heading: 'See what has been opened',
      text: 'Close the report. It now says Viewed, so everyone can tell it has been seen.\n\nWhen someone prints a report from the viewer, it says Printed, and Last Printed shows when.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/last-printed.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Food Allergies now marked Viewed in the Print Queue.',
      },
    },
    {
      id: 'details',
      part: 'Use the Print Queue',
      heading: 'See the details',
      text: 'Select a report, then Details, to see everything about it: the options it was made with, who made it and when, and why it has no data or an error.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/details.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/favorites-and-print-queue/details.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.3,
        },
        alt: 'The details of the Birthday report, saying no residents have a birthday in the date range.',
      },
    },
    {
      id: 'reprocess',
      part: 'Use the Print Queue',
      heading: 'Make it again',
      text: 'Need a fresh copy, for example after diet orders changed? Select a Completed, Viewed or Printed report, then Reprocess. Dietech makes it again with the same options, and the new copy goes to the top of the list.\n\nA report with an Error cannot be reprocessed: run it again from Reports, and call Dietech support if it fails again.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/reprocess.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/favorites-and-print-queue/reprocess.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.9,
        },
        alt: 'A new Facility Census report at the top of the Print Queue after Reprocess.',
      },
    },
    {
      id: 'all-users',
      part: 'Use the Print Queue',
      heading: 'Everyone\'s reports',
      text: 'The Print Queue lists your own reports. Select All users to see the reports everyone at your facility made, and Deleted to see reports that were deleted.',
      media: {
        image: {
          src: '/help/favorites-and-print-queue/all-users.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/favorites-and-print-queue/all-users.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.1,
        },
        alt: 'The Print Queue with All users turned on, now also listing a report made by another user.',
      },
    },
  ],
  tips: [
    'The number beside Print Queue in the menu is how many of your reports are ready and not opened yet. Check it after you generate a report.',
    'To save a report from the Print Queue, open it and use the download button: PDF, Excel or Word.',
    'A report with No Data is not broken: nothing matched its options, such as no birthdays in the date range.',
  ],
  notes: [
    'Favorites are yours. Starring a report does not change anyone else\'s list.',
    'Reprocess works for Completed, Viewed and Printed reports made in the last 72 hours. For older ones, or one with an Error, run the report again from Reports.',
  ],
  related: ['running-a-report', 'getting-around'],
  recorded: '2026-10-28',
};

export default article;
